import { useEffect, useMemo, useState } from 'react';
import {
  REFRESH_MS,
  useInsights,
  type Loaded,
} from '../catalog/pipeline/useInsights';
import {
  STATUS_COLOUR,
  STATUS_LABEL,
  statusIncidentFromWire,
  type StatusIncident,
  type StatusKey,
  type WireStatusIncident,
} from '../catalog/component/serviceStatus';

/**
 * The days the page draws a bar for, and over which it states each uptime: the
 * window the status page itself states, and the one a component's page uses,
 * so the same service never shows two uptimes in two places.
 */
export const WINDOW_DAYS = 90;

/** `public` is what status.revenexx.com shows; `all` adds the internal rest. */
export type Visibility = 'public' | 'all';

export type DayBar = {
  date: string;
  status: StatusKey;
  /** Null exactly when nothing was checked that day. */
  uptimePct: number | null;
};

export type OverviewService = {
  slug: string;
  name: string;
  visibility: 'public' | 'internal';
  status: StatusKey;
  measured: boolean;
  /** Null exactly when `measured` is false. */
  uptimePct: number | null;
  slaTargetPct: number;
  /** The public service page; null for an internal service. */
  url: string | null;
  days: DayBar[];
};

export type OverviewGroup = {
  name: string;
  slug: string;
  services: OverviewService[];
};

export type Named = { slug: string; name: string };

export type OverviewIncident = StatusIncident & {
  service: Named | null;
  latestUpdate: { state: string; body: string; postedAt: string } | null;
  linearIssueUrl: string | null;
};

export type Maintenance = {
  id: number;
  title: string;
  startsAt: string;
  endsAt: string;
  impact: string | null;
  public: boolean;
  service: Named | null;
  adminUrl: string;
};

export type Sla = {
  /** `YYYY-MM`, the calendar month in Europe/Berlin. */
  month: string;
  targetPct: number;
  /** Null when no service in the view was measured this month. */
  uptimePct: number | null;
  met: boolean | null;
  measuredServices: number;
  unmeasuredServices: number;
};

/** The status page's answer for one view (docs/status-probes.md). */
export type StatusOverview = {
  visibility: Visibility;
  windowDays: number;
  status: StatusKey;
  groups: OverviewGroup[];
  incidents: { open: OverviewIncident[]; recent: OverviewIncident[] };
  maintenance: Maintenance[];
  sla: Sla;
  links: {
    publicPage: string;
    declareIncident: string;
    incidents: string;
    slaReport: string;
  };
  generatedAt: string;
};

type WireIncident = WireStatusIncident & {
  service: Named | null;
  latest_update: { state: string; body: string; posted_at: string } | null;
  linear_issue_url: string | null;
};

/** The answer as the status service writes it. */
type Wire = {
  visibility: Visibility;
  window_days: number;
  status: StatusKey;
  groups: {
    name: string;
    slug: string;
    services: {
      slug: string;
      name: string;
      visibility: 'public' | 'internal';
      status: StatusKey;
      measured: boolean;
      uptime_pct: number | null;
      sla_target_pct: number;
      url: string | null;
      days: { date: string; status: StatusKey; uptime_pct: number | null }[];
    }[];
  }[];
  incidents: { open: WireIncident[]; recent: WireIncident[] };
  maintenance: {
    id: number;
    title: string;
    starts_at: string;
    ends_at: string;
    impact: string | null;
    public: boolean;
    service: Named | null;
    admin_url: string;
  }[];
  sla: {
    month: string;
    target_pct: number;
    uptime_pct: number | null;
    met: boolean | null;
    measured_services: number;
    unmeasured_services: number;
  };
  links: {
    public_page: string;
    declare_incident: string;
    incidents: string;
    sla_report: string;
  };
  generated_at: string;
};

const incidentFromWire = (i: WireIncident): OverviewIncident => ({
  ...statusIncidentFromWire(i),
  service: i.service,
  latestUpdate: i.latest_update && {
    state: i.latest_update.state,
    body: i.latest_update.body,
    postedAt: i.latest_update.posted_at,
  },
  linearIssueUrl: i.linear_issue_url,
});

export function fromWire(wire: Wire): StatusOverview {
  return {
    visibility: wire.visibility,
    windowDays: wire.window_days,
    status: wire.status,
    groups: wire.groups.map(group => ({
      name: group.name,
      slug: group.slug,
      services: group.services.map(service => ({
        slug: service.slug,
        name: service.name,
        visibility: service.visibility,
        status: service.status,
        measured: service.measured,
        uptimePct: service.measured ? service.uptime_pct : null,
        slaTargetPct: service.sla_target_pct,
        url: service.url,
        days: service.days.map(day => ({
          date: day.date,
          status: day.status,
          uptimePct: day.status === 'no_data' ? null : day.uptime_pct,
        })),
      })),
    })),
    incidents: {
      open: wire.incidents.open.map(incidentFromWire),
      recent: wire.incidents.recent.map(incidentFromWire),
    },
    maintenance: wire.maintenance.map(m => ({
      id: m.id,
      title: m.title,
      startsAt: m.starts_at,
      endsAt: m.ends_at,
      impact: m.impact,
      public: m.public,
      service: m.service,
      adminUrl: m.admin_url,
    })),
    sla: {
      month: wire.sla.month,
      targetPct: wire.sla.target_pct,
      uptimePct: wire.sla.uptime_pct,
      met: wire.sla.met,
      measuredServices: wire.sla.measured_services,
      unmeasuredServices: wire.sla.unmeasured_services,
    },
    links: {
      publicPage: wire.links.public_page,
      declareIncident: wire.links.declare_incident,
      incidents: wire.links.incidents,
      slaReport: wire.links.sla_report,
    },
    generatedAt: wire.generated_at,
  };
}

const pathOf = (visibility: Visibility) =>
  `/status/overview?visibility=${visibility}&days=${WINDOW_DAYS}`;

// A status page is read for what is true now, so it asks again every minute
// while the tab is visible — the status service caches its figures for as long.
const always = () => true;

/**
 * Both views of the status page, asked through the backend's `/status` proxy,
 * which adds the token. Both are asked up front, so switching between them
 * shows the other answer at once rather than a spinner; each is the status
 * service's own, so the page computes no figure of its own.
 *
 * `refreshWhile` only asks again once there is an answer, so a view whose
 * first ask failed is asked again here, every minute while the tab is visible,
 * until it answers — the page says it reads again every minute.
 */
export function useStatusOverviews(): Record<
  Visibility,
  Loaded<StatusOverview>
> {
  const [retries, setRetries] = useState<Record<Visibility, number>>({
    public: 0,
    all: 0,
  });
  const customerFacing = useInsights<Wire>(pathOf('public'), {
    plugin: 'proxy',
    refreshWhile: always,
    refreshKey: String(retries.public),
  });
  const internal = useInsights<Wire>(pathOf('all'), {
    plugin: 'proxy',
    refreshWhile: always,
    refreshKey: String(retries.all),
  });
  const publicFailed = !customerFacing.loading && !customerFacing.data;
  const allFailed = !internal.loading && !internal.data;
  useEffect(() => {
    if (!publicFailed && !allFailed) return undefined;
    const id = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      setRetries(r => ({
        public: r.public + (publicFailed ? 1 : 0),
        all: r.all + (allFailed ? 1 : 0),
      }));
    }, REFRESH_MS);
    return () => window.clearInterval(id);
  }, [publicFailed, allFailed]);
  return useMemo(() => {
    const read = (answer: Loaded<Wire>): Loaded<StatusOverview> =>
      answer.data
        ? { loading: false, data: fromWire(answer.data) }
        : { loading: answer.loading, error: answer.error };
    return { public: read(customerFacing), all: read(internal) };
  }, [customerFacing, internal]);
}

/** Minutes between two instants, never negative. */
export const minutesBetween = (from: string, to: number) =>
  Math.max(0, Math.floor((to - new Date(from).getTime()) / 60_000));

/** An incident's length the way the status page writes one: `22 min`, `1 h 04 min`, `2 d 3 h`. */
export function formatLength(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} h ${String(minutes % 60).padStart(2, '0')} min`;
  }
  const days = Math.floor(hours / 24);
  return hours % 24 ? `${days} d ${hours % 24} h` : `${days} d`;
}

/** How long ago, in whole days past the first: `today`, `yesterday`, `3 d ago`. */
export function formatDaysAgo(iso: string, now: number): string {
  const days = Math.floor((now - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} d ago`;
}

// Months and weekdays spelled here rather than by Intl, whose short forms
// differ between ICU versions — `Sep` on one Node, `Sept` on the next.
const MONTHS = 'Jan Feb Mar Apr May Jun Jul Aug Sep Oct Nov Dec'.split(' ');
const WEEKDAYS = 'Sun Mon Tue Wed Thu Fri Sat'.split(' ');

const BERLIN_PARTS = new Intl.DateTimeFormat('en-GB', {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  timeZone: 'Europe/Berlin',
});

/** A day as Berlin reckons it: `Sat 26 Sep`. */
const berlinDay = (at: Date) => {
  const part = (type: string) =>
    Number(BERLIN_PARTS.formatToParts(at).find(p => p.type === type)?.value);
  const [year, month, day] = [part('year'), part('month'), part('day')];
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return `${WEEKDAYS[weekday]} ${day} ${MONTHS[month - 1]}`;
};
const BERLIN_TIME = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Europe/Berlin',
});
const BERLIN_ZONE = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Berlin',
  timeZoneName: 'short',
});

const zoneOf = (at: Date) =>
  BERLIN_ZONE.formatToParts(at).find(part => part.type === 'timeZoneName')
    ?.value ?? '';

/**
 * A maintenance window in Berlin time, the way the status page states its own:
 * `Sat 26 Sep · 06:00–07:00 CEST`, with the end's day when it ends on another,
 * and the start's own zone when the clocks change in between.
 */
export function formatWindow(startsAt: string, endsAt: string): string {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  const startDay = berlinDay(start);
  const endDay = berlinDay(end);
  const startZone = zoneOf(start);
  const endZone = zoneOf(end);
  const from =
    startZone === endZone
      ? BERLIN_TIME.format(start)
      : `${BERLIN_TIME.format(start)} ${startZone}`;
  const until =
    endDay === startDay
      ? BERLIN_TIME.format(end)
      : `${endDay} ${BERLIN_TIME.format(end)}`;
  return `${startDay} · ${from}–${until} ${endZone}`;
}

/** An instant's time in Berlin with its zone, as every time on the page is: `12:05 CEST`. */
export const formatBerlinTime = (iso: string) => {
  const at = new Date(iso);
  return `${BERLIN_TIME.format(at)} ${zoneOf(at)}`;
};

/** The month an SLA covers, for a reader: `October 2026`. */
export function formatMonth(month: string): string {
  const [year, number] = month.split('-').map(Number);
  return new Intl.DateTimeFormat('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, number - 1, 15)));
}

/** Worst first, so a row's summary opens on what matters. */
const SUMMARY_ORDER: StatusKey[] = [
  'major',
  'partial',
  'degraded',
  'maintenance',
  'operational',
  'no_data',
];

/**
 * What a row of bars says, for a reader who cannot see them:
 * `1 day major outage, 2 days operational, 87 days without data`.
 */
export function summariseDays(days: DayBar[]): string {
  const counts = new Map<StatusKey, number>();
  for (const day of days) {
    counts.set(day.status, (counts.get(day.status) ?? 0) + 1);
  }
  return SUMMARY_ORDER.filter(status => counts.has(status))
    .map(status => {
      const n = counts.get(status) ?? 0;
      const what =
        status === 'no_data'
          ? 'without data'
          : STATUS_LABEL[status].toLowerCase();
      return `${n} ${n === 1 ? 'day' : 'days'} ${what}`;
    })
    .join(', ');
}

/** A bar's day, `2 Oct` — the status service counts its days in UTC. */
export const formatDay = (date: string) => {
  const [, month, day] = date.split('-').map(Number);
  return `${day} ${MONTHS[month - 1]}`;
};

/** An incident's state as a reader is told it: `Investigating`. */
export const stateLabel = (state: string) =>
  state ? state[0].toUpperCase() + state.slice(1) : state;

/** An incident's colour by its severity, in the status page's own states. */
export const SEVERITY_STATUS: Record<string, StatusKey> = {
  major: 'major',
  minor: 'degraded',
  maintenance: 'maintenance',
};

/**
 * What an incident's severity means, said in words beside its colour, so the
 * colour is never the only thing that says it.
 */
export const SEVERITY_LABEL: Record<string, string> = {
  major: 'Major outage',
  minor: 'Degraded performance',
  maintenance: 'Under maintenance',
};

const SEVERITY_RANK: Record<string, number> = {
  major: 0,
  minor: 1,
  maintenance: 2,
};

/**
 * The incidents worst first, and within a severity in the order given —
 * newest first, as the status page answers — so a major outage never stands
 * beneath a degradation that merely started later.
 */
export const bySeverity = (incidents: OverviewIncident[]) =>
  [...incidents].sort(
    (a, b) =>
      (SEVERITY_RANK[a.severity] ?? 1) - (SEVERITY_RANK[b.severity] ?? 1),
  );

/** Open incidents alike — one alert open several times — as one entry. */
export type IncidentGroup = {
  /** Every incident the entry stands for, the one that started first first. */
  incidents: OverviewIncident[];
  /** The one that has run longest: it says how long this has been going on. */
  oldest: OverviewIncident;
  /**
   * The one heard from last — by its latest update, else by its start: its
   * state and its update are what is true now, and it is the one to open.
   */
  current: OverviewIncident;
  count: number;
};

const at = (iso: string) => Date.parse(iso);

const lastHeard = (incident: OverviewIncident) =>
  at(incident.latestUpdate?.postedAt ?? incident.startedAt);

/**
 * The open incidents with those alike folded into one entry, each entry where
 * its first member stood. Alike means the same severity, service, title and
 * publicity: the status page opens one incident per alert series, so a rule
 * firing for three replicas opens three incidents of one name, which a reader
 * takes for one outage shown three times. A public incident never folds into
 * an internal one, so neither takes the other's label or link.
 */
export function groupIncidents(incidents: OverviewIncident[]): IncidentGroup[] {
  const groups = new Map<string, OverviewIncident[]>();
  for (const incident of incidents) {
    const key = [
      incident.severity,
      incident.public ? 'public' : 'internal',
      incident.service?.slug ?? '',
      incident.title,
    ].join('\u0000');
    groups.set(key, [...(groups.get(key) ?? []), incident]);
  }
  return [...groups.values()].map(members => {
    const byStart = [...members].sort(
      (a, b) => at(a.startedAt) - at(b.startedAt),
    );
    const current = members.reduce((latest, incident) =>
      lastHeard(incident) > lastHeard(latest) ? incident : latest,
    );
    return {
      incidents: byStart,
      oldest: byStart[0],
      current,
      count: members.length,
    };
  });
}

/**
 * The first day a service was checked, when that is after the window began:
 * its uptime then covers fewer days than the window, and a reader must not
 * take it for the window's. Null for a service checked from the window's
 * first day, and for one never checked.
 */
export function measuredSince(days: DayBar[]): string | null {
  const first = days.findIndex(day => day.uptimePct !== null);
  return first > 0 ? days[first].date : null;
}

/** An incident's colour, by its severity; an unknown one reads as degraded. */
export const severityColour = (severity: string) =>
  STATUS_COLOUR[SEVERITY_STATUS[severity] ?? 'degraded'];

/** What an incident is called: `Webhooks · Delivery delayed`. */
export const incidentName = (incident: OverviewIncident) =>
  incident.service
    ? `${incident.service.name} · ${incident.title}`
    : incident.title;

/** Where an incident is read: its public page, else its page in the admin. */
export const incidentHref = (incident: OverviewIncident) =>
  incident.url ?? incident.adminUrl;
